export default {
  switchOrganization: 'Switch organization',
  noOrganizations: 'No organizations found',
  createNew: 'Create organization',
  createSubtitle: 'New workspace',
  manageOrganizations: 'Manage organizations',
  current: 'Current',
  switchTo: 'Switch to',
  searchPlaceholder: 'Search organizations...',
  noResults: 'No organizations found',
  errorLoadingOrganizations: 'Error loading organizations',
  memberCount: '{count} member | {count} members',
  members: 'members',
  inviteLabel: 'Invite members',
  inviteSubtitle: 'Invite people to your organization',
  // System role display names (org + workspace). Custom roles fall back to
  // their stored name. Lives here (not settings) so role-name translation
  // works wherever the organizations app is used.
  roles: {
    admin: 'Administrator',
    editor: 'Editor',
    member: 'Member',
    guest: 'Guest',
    viewer: 'Viewer',
    owner: 'Owner',
  },
  list: {
    title: 'Organizations',
    subtitle: 'Manage your organizations and teams',
  },
  card: {
    owner: 'Owner',
  },
  switching: {
    loading: 'Switching organization...',
  },
  legacy: {
    badge: 'Legacy interface',
  },
  create: {
    title: 'Create organization',
    subtitle: 'A separate space, with its own members and billing',
    modalSubtitle: 'A separate space, with its own members and billing.',
    previewPlaceholder: 'Your organization',
    nextStepsNote: 'Logo, language and members are set up right after, in the settings.',
    error: 'Creation failed. Try again in a moment.',
  },
  form: {
    nameTitle: 'Organization name',
    nameDescription: 'Name visible to all members.',
    namePlaceholder: 'e.g. Acme Corp',
    nameRequired: 'Name is required',
    createButton: 'Create organization',
  },
  invite: {
    title: 'Invite to organization',
    titleSpace: 'Invite to space',
    context: {
      org: 'Organization',
      space: 'Space',
    },
    header: {
      members: '{count} member(s)',
      expires: 'Expires',
    },
    expiry: {
      today: 'today',
      tomorrow: 'tomorrow',
      inDays: 'in {days} days',
      inWeeks: 'in {weeks} week(s)',
      onDate: 'on {date}',
    },
    link: {
      title: 'Link access',
      description: 'Anyone with the link can join.',
      create: 'Create invitation link',
      copy: 'Copy link',
      regenerate: 'Regenerate',
      openNew: 'Open in new tab',
      expires: 'This link will expire',
      expiresNever: 'never',
      modifySettings: 'Modify in settings',
      protected: 'Password protected',
      maxUses: 'Max: {count} use(s)',
    },
    email: {
      title: 'Invite people',
      description:
        'Invitees get access even without the link. They will receive a unique invitation link by email.',
      placeholder: 'Add recipients',
      hint: 'Type an email then press Enter',
      send: 'Send invitations',
      bulkImport: 'Bulk import',
      sent: 'Invitations sent',
      viewMembers: 'View members ({count})',
      invalidEmails: 'Some email addresses are invalid',
    },
    settings: {
      title: 'Invitation link settings',
      shortTitle: 'Settings',
      info: 'These settings apply to the invitation link. Email invitations are always valid.',
      actions: 'Actions',
      maxUses: {
        title: 'Usage limit',
        placeholder: 'Select a limit',
        hint: 'Link will be disabled after reaching this limit',
        unlimited: 'Unlimited',
        uses: '{count} use(s)',
      },
      expiration: {
        title: 'Validity period',
        placeholder: 'Select duration',
        hint: 'Link will automatically expire',
        never: 'Never',
        '1d': '1 day',
        '7d': '7 days',
        '15d': '15 days',
        '30d': '30 days',
      },
      regenerate: 'Generate new link',
      regenerateHint: 'Creates a new invitation link. The old link will stay active until you deactivate it.',
      generateBtn: 'Create new link',
      manageHistory: 'Manage links in history',
      manageHistoryHint: 'View all links, activate/deactivate them.',
    },
    history: {
      title: 'Invitation link history',
      cardSubtitle: 'Manage and view all generated links',
      infoCleanup: 'Expired and inactive links older than 90 days are automatically deleted.',
      empty: 'No links generated',
      emptyDescription: 'Link history will appear here once you generate invitation links.',
      tabAll: 'All',
      active: 'Active',
      inactive: 'Inactive',
      activate: 'Activate link',
      deactivate: 'Deactivate link',
      expired: 'Expired',
      exhausted: 'Exhausted',
      untitled: 'Untitled link',
      createdBy: 'by {name}',
      createdOn: 'Created {date}',
      expiresIn: 'Expires in {days} days',
      noExpiry: 'No expiry',
      usageProgress: '{current} / {max} uses',
      usageUnlimited: '{count} use(s)',
    },
    import: {
      title: 'Bulk import',
      infoTitle: 'Smart email import',
      info: 'Paste any text containing email addresses. The system will automatically detect all valid emails, regardless of format.',
      label: 'Email list',
      placeholder: 'Paste your text here...',
      detected: '{count} email(s) detected',
      preview: 'Preview',
      valid: '{count} valid',
      invalid: '{count} invalid',
      duplicates: '{count} duplicate(s) removed',
      add: 'Add {count} email(s)',
      clear: 'Clear all',
      examplesTitle: 'Examples of accepted text formats',
    },
    viewMembers: {
      title: 'View members',
      subtitle: '{count} members in this organization',
    },
    errors: {
      alreadyMembers: 'These emails are already members of this organization:',
      sendFailed: 'Failed to send invitations to:',
    },
    roleWorkspace: {
      title: 'Role & workspaces',
      description: 'Pick the organization role and, for each workspace, the role to assign there.',
      roleLabel: 'Organization role',
      workspaceLabel: 'Add to a space',
      workspacePlaceholder: 'Select a space to add…',
      workspaceAllSelected: 'All spaces already added',
    },
    mode: {
      switchToLink: 'Create a shareable link',
      switchToLinkHint: 'Share one link so people can join — no need to enter their emails.',
      switchToEmail: 'Back to email invitation',
      linkHistory: 'Link history',
      linkSectionTitle: 'Invitation link',
      recipientsTitle: 'Invite people',
      recipientsDescription: 'They will each receive a personal invitation by email.',
    },
    access: {
      generalTitle: 'General access',
      generalDescription: 'The organization role everyone in this invitation receives.',
      spaceTitle: 'Access to this space',
      customizeTitle: 'Grant access to private spaces',
      customizeDescription: 'Public spaces are already visible to members. Grant access to specific private spaces here.',
      addWorkspace: 'Add a space',
      thisSpaceBadge: 'This space',
      memberHint: 'Members automatically see all public spaces as Viewer.',
      adminHint: 'This role grants access to every space as Administrator.',
      searchPlaceholder: 'Search a private space…',
      defaultRole: 'Default role',
      selectedCount: '{count} selected',
      selectAll: 'Select all',
      deselectAll: 'Clear',
      noResults: 'No space matches your search',
    },
    preview: {
      title: 'What they will get access to',
      allWorkspaces: 'All spaces',
      grantedTitle: 'Spaces you grant',
      publicTitle: 'Public spaces (visible by default)',
      publicCount: '{count} public space(s)',
      autoViewerNote: 'visible by default',
      publicBadge: 'Public',
      privateBadge: 'Private',
      noWorkspaces: 'Organization membership only — no space access.',
      noWorkspacesInOrg: 'This organization has no spaces yet.',
      toggleNames: 'Details',
    },
    review: {
      title: 'Review before sending',
      recipients: 'Recipients',
      orgRole: 'Organization role',
      willSee: 'They will get access to',
      editButton: 'Edit',
      sendButton: 'Send {count} invitation(s)',
    },
    method: {
      link: 'Shareable link',
      email: 'By email',
    },
    form: {
      nameLabel: 'Link name (optional)',
      nameHelper: 'Only to recognize it later — a dated name is used if you leave it empty.',
      namePlaceholder: 'e.g. Q1 onboarding · marketing team',
      defaultName: 'Share · {date}',
      quotasTitle: 'Limits',
      generateButton: 'Generate link',
      sendButton: 'Send invitations',
    },
    result: {
      title: 'Invitation link generated',
      description: '"{name}" is ready to share. Once generated, a link is read-only — create a new one if you need different settings.',
      summary: 'Configuration',
      orgRole: 'Organization role',
      workspaces: 'Workspaces',
      noWorkspaces: 'No workspaces',
      createAnother: 'Create another link',
    },
    toast: {
      linkCreated: 'Invitation link created',
      linkCreateFailed: 'Failed to create link',
      linkActivated: 'Link activated',
      linkDeactivated: 'Link deactivated',
      linkUpdated: 'Link settings updated',
      linkUpdateFailed: 'Failed to update link',
      linkCopied: 'Link copied to clipboard',
      invitesSent: '{count} invitation(s) sent',
      invitesFailed: 'Failed to send invitations',
      emailsAdded: '{count} email(s) added',
    },
  },
}
